/**
 * DEV-ONLY Diagnostic Logging Utility
 * 
 * Purpose: Safe cleanup phase diagnostic logging
 * Only outputs in development environment (NODE_ENV !== 'production')
 * 
 * Usage:
 *   import { devLog } from '../utils/devLogger';
 *   devLog.command('Command opened');
 *   devLog.intent('help', { parsed: 'HELP_REQUEST' });
 *   devLog.modal('RunAgentModal', { variant: 'text_input' });
 * 
 * Tags: [SAFE_CLEANUP] prefix for easy filtering
 */

const isDev = process.env.NODE_ENV !== 'production';

/**
 * Core logging function - only outputs in development
 */
const log = (category, message, data = null) => {
  if (!isDev) return;
  
  const tag = `[SAFE_CLEANUP][${category}]`;
  
  if (data) {
    console.debug(tag, message, data);
  } else {
    console.debug(tag, message);
  }
};

/**
 * Dev Logger object with categorized methods
 */
export const devLog = {
  /**
   * Command drawer state changes
   * @param {string} message - Description of state change
   * @param {object} data - Optional data payload
   */
  command: (message, data = null) => log('COMMAND', message, data),
  
  /**
   * Command intent parsing
   * @param {string} rawInput - The raw user input
   * @param {object} parsedIntent - The parsed intent result
   */
  intent: (rawInput, parsedIntent) => log('INTENT', `"${rawInput}" →`, parsedIntent),
  
  /**
   * Run modal state
   * @param {string} agentName - Agent being run
   * @param {object} modalConfig - Modal configuration/variant
   */
  modal: (agentName, modalConfig) => log('MODAL', `Opening for "${agentName}"`, modalConfig),
  
  /**
   * Agent selection/fetch
   * @param {string} message - Description
   * @param {object} data - Agent data
   */
  agent: (message, data = null) => log('AGENT', message, data),
  
  /**
   * Organisation context changes
   * @param {string} message - Description
   * @param {object} data - Org data
   */
  org: (message, data = null) => log('ORG', message, data),
  
  /**
   * Access control checks
   * @param {string} message - Description
   * @param {object} data - Permission data
   */
  access: (message, data = null) => log('ACCESS', message, data),
  
  /**
   * Error logging (safe formatting)
   * @param {string} context - Where the error occurred
   * @param {Error|string|object} error - The error
   */
  error: (context, error) => {
    if (!isDev) return;
    
    const tag = '[SAFE_CLEANUP][ERROR]';
    const errorMessage = error instanceof Error 
      ? error.message 
      : typeof error === 'string' 
        ? error 
        : JSON.stringify(error);
    
    console.debug(tag, `${context}:`, errorMessage);
  },
  
  /**
   * Generic debug log
   * @param {string} message - Message
   * @param {object} data - Optional data
   */
  debug: (message, data = null) => log('DEBUG', message, data),
};

/**
 * Safe error formatter for UI display
 * Prevents [object Object] from showing in UI
 * 
 * @param {Error|string|object|null} err - The error to format
 * @param {string} fallback - Fallback message if error is empty
 * @returns {string} Human-readable error message
 */
export const formatUiError = (err, fallback = 'An unexpected error occurred') => {
  if (!err) return fallback;
  
  // String error
  if (typeof err === 'string') {
    return err.trim() || fallback;
  }
  
  // Error object with message
  if (err instanceof Error) {
    return err.message || fallback;
  }
  
  // Object with message property
  if (typeof err === 'object') {
    // API error response
    if (err.detail) return err.detail;
    if (err.message) return err.message;
    if (err.error) return typeof err.error === 'string' ? err.error : fallback;
    
    // Prevent [object Object]
    try {
      const str = JSON.stringify(err);
      // If it's just "{}" or too long, use fallback
      if (str === '{}' || str.length > 200) return fallback;
      return str;
    } catch {
      return fallback;
    }
  }
  
  return fallback;
};

export default devLog;
